import { useState, useEffect, useRef } from 'react';
import { Outlet, Navigate, useNavigate, Link, useLocation } from 'react-router';
import { useAuth } from '../lib/auth-context';
import { InstallAppButton } from './InstallAppButton';
import { useI18n, translateDbValue } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import {
  LayoutDashboard,
  FileText,
  Package,
  Truck,
  Users,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronRight,
  ChevronDown,
  User,
  Bell,
  CircleUser,
  CreditCard,
  MessageSquare,
  Megaphone,
  MessageCircle,
  Mail,
  ArrowUpDown,
  Scissors,
  Palette,
  Smartphone,
  Shield,
  Crown,
  DollarSign,
  Wallet,
  Calculator,
  BarChart3,
  LifeBuoy,
  ChevronLeft,
  Menu as MenuIcon,
  ShoppingCart,
  Gift,
  ClipboardList,
  ReceiptText,
  Shirt,
  Tags,
  ListOrdered,
  SlidersHorizontal,
  UserCog,
  FileSpreadsheet,
  PieChart,
  TrendingDown,
  Printer,
  Navigation,
  Building2,
  Inbox,
  Car,
  Armchair,
} from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from './ui/tooltip';
import { settingsApi, inviteApi, dashboardApi } from '../lib/api';
import { APP_VERSION, BUILD_DATE } from '../version';
import { hasUnseenRelease, whatsNewUi } from '../lib/whats-new';
import { useCallerId, isCallerIdSupported, type IncomingCall } from '../lib/caller-id';
import { IncomingCallModal, useIncomingCallModal } from './IncomingCallModal';
import { customersApi, serviceRequestsApi, fetchApi } from '../lib/api';

import { tx } from '../lib/tx';
import { PlanProvider, usePlan } from '../lib/plan-context';
import { PlanBanners } from './PlanBanners';
import { closedAccountView, isOwnerRole } from '../lib/plan-gates';
import { planUiText } from '../lib/plan-texts';
import { forceRelogin, PLAN_FREE_ROLE_NOTICE_KEY } from '../lib/session-token';

// Kapalı hesapta (abonelik bitişi + 90 gün) yalnız abonelik/ödeme sayfaları açılır
const CLOSED_ALLOWED_PATHS = ['/subscription', '/payment'];
const isClosedAllowedPath = (path: string) =>
  CLOSED_ALLOWED_PATHS.some((p) => path === p || path.startsWith(p + '/'));

function MainLayoutComponent() {
  const { user, logout, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language, refreshSettings } = useI18n();
  const { access } = usePlan();

  // Girişten sonra ayarları (para birimi, dil) backend'den bir kez eşitle.
  // LanguageContext bunu yalnızca uygulama ilk açılırken yapıyor; giriş sayfasında kullanıcı
  // olmadığı için atlıyor ve ₽ gibi para birimi TL önbelleğinde kalıyordu.
  useEffect(() => { refreshSettings(); }, []);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    try { return JSON.parse(localStorage.getItem('cleandy:menuGroups') || '{}'); } catch { return {}; }
  });
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    // Daha önce zaten daraltılmış mıydı?
    return localStorage.getItem('sidebarCollapsed') === 'true';
  });
  const [companyLogo, setCompanyLogo] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string | null>(null);
  const [defaultServiceType, setDefaultServiceType] = useState(() => {
    try {
      const stored = localStorage.getItem('customSettings');
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed.default_service_type || parsed.defaultServiceType || '';
      }
    } catch {}
    return '';
  });
  // Sektör sunucudan teyit edildi mi? (localStorage'daki değer başka firmadan kalmış olabilir)
  const [serviceTypeConfirmed, setServiceTypeConfirmed] = useState(false);
  const carWashModuleEnabled   = defaultServiceType === 'carWash';
  const berberModuleEnabled    = defaultServiceType === 'barber';
  const sofaWashModuleEnabled  = defaultServiceType === 'sofaWashing';

  // [Türkçe, İngilizce] — diğer diller tx() sözlüğünden
  const SERVICE_LABELS: Record<string, [string, string]> = {
    barber:        ['Berber / Kuaför', 'Barber / Salon'],
    carWash:       ['Oto Yıkama', 'Car Wash'],
    dryCleaning:   ['Kuru Temizleme', 'Dry Cleaning'],
    washing:       ['Çamaşır Yıkama', 'Laundry'],
    ironing:       ['Ütüleme', 'Ironing'],
    dyeing:        ['Boyama', 'Dyeing'],
    tailor:        ['Terzi', 'Tailor'],
    tailoring:     ['Terzi / Tadilat', 'Tailoring / Alterations'],
    shoeShine:     ['Lostra', 'Shoe Shine'],
    carpetWashing: ['Halı Yıkama', 'Carpet Cleaning'],
    sofaWashing:   ['Koltuk Yıkama', 'Sofa Cleaning'],
  };
  const serviceLabel = tx(language, ...(SERVICE_LABELS[defaultServiceType] || SERVICE_LABELS.dryCleaning));
  const [incomingCount, setIncomingCount] = useState(0);
  const prevIncomingCount = useRef(0);
  const [hazirCount, setHazirCount] = useState(0);
  const [teslimCount, setTeslimCount] = useState(0);
  const [duyuruCount, setDuyuruCount] = useState(0);

  // Caller ID state
  const callerIdSupported = isCallerIdSupported();
  const {
    status: callerIdStatus,
    connect: connectCallerId,
    disconnect: disconnectCallerId,
  } = useCallerId();
  
  const { 
    currentCall, 
    showModal: showCallerModal, 
    handleIncomingCall,
    dismissCall: dismissCallerModal,
  } = useIncomingCallModal();
  
  const [callerIdCustomer, setCallerIdCustomer] = useState<{id: string; name: string; phone: string; address?: string; notes?: string} | null>(null);

  // İlk girişte sidebar geniş açılıp 1.2sn sonra kapanır (bir kez)
  useEffect(() => {
    if (localStorage.getItem('sidebarCollapsed') === null) {
      setIsSidebarCollapsed(false);
      const timer = setTimeout(() => {
        setIsSidebarCollapsed(true);
        localStorage.setItem('sidebarCollapsed', 'true');
      }, 1200);
      return () => clearTimeout(timer);
    }
  }, []);

  // ✅ AUTH CHECK: Redirect to login if not authenticated
  useEffect(() => {
    // Auth context henüz yükleniyorsa bekle
    if (isLoading) {
      console.log('[MainLayout] Auth is loading, waiting...');
      return;
    }

    const tenantId = localStorage.getItem('tenantId');
    const savedUser = localStorage.getItem('user');

    console.log('[MainLayout] Auth check:', {
      hasUser: !!user,
      hasSavedUser: !!savedUser,
      hasTenantId: !!tenantId,
      currentPath: location.pathname,
      isLoading
    });

    if (!user && !savedUser) {
      console.log('[MainLayout] No user found - redirecting to login');
      navigate('/login', { replace: true });
      return;
    }

    if (!tenantId) {
      // Firma kodu silinmiş ama oturum açık (ör. başka sekmede giriş ekranı silmiş):
      // kullanıcıda firma varsa geri yaz; yoksa oturumu kapat. Yalnız yönlendirmek,
      // giriş ekranı açık oturumu görüp '/'a geri gönderdiği için sonsuz döngü yaratıyordu.
      if (user?.tenantId) {
        localStorage.setItem('tenantId', user.tenantId);
        return;
      }
      console.log('[MainLayout] No tenantId found - logging out');
      logout();
      navigate('/login', { replace: true });
      return;
    }
  }, [user, navigate, location.pathname, isLoading]);

  // USD/TRY kuru — günde 1 kez çek (jsDelivr CDN üzerinden fawazahmed currency-api)
  useEffect(() => {
    const today = new Date().toDateString();
    const lastFetchDate = localStorage.getItem('usdToTryRateDate');
    const cachedRate = localStorage.getItem('usdToTryRate');
    // Stale check: eğer 45'ten küçükse eski veri, yeniden çek
    if (lastFetchDate === today && cachedRate && parseFloat(cachedRate) > 45) return;

    const fetchRate = async () => {
      try {
        const res = await fetch(
          'https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/usd.json',
          { cache: 'no-store' }
        );
        if (!res.ok) throw new Error('primary failed');
        const data = await res.json();
        const rate = data?.usd?.try;
        if (rate && rate > 30) {
          localStorage.setItem('usdToTryRate', rate.toString());
          localStorage.setItem('usdToTryRateDate', today);
        }
      } catch {
        // Fallback: open.er-api.com
        try {
          const res2 = await fetch('https://open.er-api.com/v6/latest/USD');
          if (!res2.ok) return;
          const d = await res2.json();
          const rate = d?.rates?.TRY;
          if (rate && rate > 30) {
            localStorage.setItem('usdToTryRate', rate.toString());
            localStorage.setItem('usdToTryRateDate', today);
          }
        } catch {}
      }
    };

    fetchRate();
  }, []);

  // Davet kodu varsa login sonrası otomatik bağla
  useEffect(() => {
    if (!user || isLoading) return;
    const pendingCode = localStorage.getItem('pendingInviteCode');
    if (!pendingCode) return;
    localStorage.removeItem('pendingInviteCode');
    inviteApi.redeem(pendingCode)
      .then((result: any) => {
        toast.success('Firma bağlandı!');
        const dest = result?.request_id ? `/incoming-requests?r=${result.request_id}` : '/incoming-requests';
        navigate(dest);
      })
      .catch(() => {
        navigate('/incoming-requests');
      });
  }, [user, isLoading]);

  useEffect(() => {
    const fetchCompanyLogo = async () => {
      try {
        const data = await settingsApi.get();
        console.log('🖼️ [MainLayout] Settings received:', JSON.stringify(data, null, 2));
        const settings = data.settings || data;
        const logoUrl = settings.receiptSettings?.logoUrl || settings.logoUrl || null;
        console.log('🖼️ [MainLayout] Logo URL extracted:', logoUrl);
        if (logoUrl) {
          setCompanyLogo(logoUrl);
        }
        const name = settings.company_name || settings.companyName || null;
        if (name) {
          setCompanyName(name);
        }
        let dst = settings.default_service_type || settings.defaultServiceType || '';

        // İlk girişte signup'tan gelen sektör seçimini API'ye kaydet
        const pendingDst = localStorage.getItem('pendingDefaultService');
        if (pendingDst && (!dst || dst === 'dryCleaning')) {
          dst = pendingDst;
          localStorage.removeItem('pendingDefaultService');
          try {
            await settingsApi.update({ default_service_type: dst });
          } catch { /* sessizce geç */ }
        }

        setDefaultServiceType(dst);
        setServiceTypeConfirmed(true);
        // Tarayıcıda kalan sektör bilgisini bu firmanınkiyle eşitle (firma değişince eskisi kalmasın)
        try {
          const cs = JSON.parse(localStorage.getItem('customSettings') || '{}');
          cs.default_service_type = dst;
          delete cs.defaultServiceType;
          localStorage.setItem('customSettings', JSON.stringify(cs));
        } catch { /* yoksay */ }
        // Abonelik / paket durumu PlanProvider'da (GET /tenant/me → access)
      } catch (error) {
        console.error('Error fetching company logo:', error);
      }
    };

    fetchCompanyLogo();

    const handleLogoUpdated = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail) {
        setCompanyLogo(customEvent.detail);
      }
    };

    const handleCompanyNameUpdated = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail) {
        setCompanyName(customEvent.detail);
      }
    };

    const handleDefaultServiceUpdated = (e: Event) => {
      const dst = (e as CustomEvent).detail;
      setDefaultServiceType(dst);
    };

    window.addEventListener('logoUpdated', handleLogoUpdated);
    window.addEventListener('companyNameUpdated', handleCompanyNameUpdated);
    window.addEventListener('defaultServiceTypeUpdated', handleDefaultServiceUpdated);
    return () => {
      window.removeEventListener('logoUpdated', handleLogoUpdated);
      window.removeEventListener('companyNameUpdated', handleCompanyNameUpdated);
      window.removeEventListener('defaultServiceTypeUpdated', handleDefaultServiceUpdated);
    };
  }, []);

  // Sayfa başlığını hizmet türüne göre güncelle
  useEffect(() => {
    document.title = `Cleandy ${serviceLabel}`;
  }, [serviceLabel]);

  // Gelen talepler badge — 60 saniyede bir polling
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const data = await serviceRequestsApi.incomingCount();
        const newCount = data?.count ?? 0;
        if (newCount > prevIncomingCount.current && prevIncomingCount.current >= 0) {
          const diff = newCount - prevIncomingCount.current;
          if (prevIncomingCount.current > 0) {
            // Sonraki kontrollerde yeni gelenleri bildir
            import('sonner').then(({ toast }) =>
              toast(`📦 ${diff} yeni teklif talebi geldi`, { duration: 5000 })
            );
          }
        }
        prevIncomingCount.current = newCount;
        setIncomingCount(newCount);
      } catch {
        // sessiz hata — badge gösterme
      }
    };

    fetchCount(); // ilk yükleme
    const interval = setInterval(fetchCount, 60_000);
    return () => clearInterval(interval);
  }, []);

  // Sidebar badge'leri: hazır sipariş, teslimde, duyuru — 5 dakikada bir yenile
  useEffect(() => {
    const fetchBadges = async () => {
      try {
        const data = await dashboardApi.getStats('today');
        const byStatus = data?.stats?.ordersByStatus || data?.ordersByStatus || {};
        setHazirCount(byStatus['hazır'] || byStatus['hazir'] || 0);
        setTeslimCount(byStatus['teslim'] || 0);
      } catch {}

      try {
        const tenantId = localStorage.getItem('tenantId') || '';
        const userId = localStorage.getItem('user_id') || '';
        if (!tenantId) return;
        // fetchApi: temizlenmiş tenant başlığı + oturum token'ı ile (eskiden tanımsız projectId yüzünden hep hata veriyordu)
        const d = await fetchApi(`/announcements/${tenantId}`);
        const anns: any[] = d?.announcements || [];
        const unread = anns.filter(a => !a.readBy?.includes(userId)).length;
        setDuyuruCount(unread);
      } catch {}
    };

    fetchBadges();
    const interval = setInterval(fetchBadges, 300_000); // 5 dakika
    return () => clearInterval(interval);
  }, []);

  // Caller ID: Auto-connect on startup if enabled
  useEffect(() => {
    const autoConnect = localStorage.getItem('callerIdAutoConnect');
    if (autoConnect === 'true' && callerIdSupported && !callerIdStatus.connected) {
      connectCallerId();
    }
  }, []);

  // Caller ID: Listen for incoming calls and lookup customer
  useEffect(() => {
    if (callerIdStatus.lastCall) {
      const phone = callerIdStatus.lastCall.phoneNumber;
      console.log('📞 [MainLayout] Incoming call detected:', phone);
      
      // Show the modal
      handleIncomingCall(callerIdStatus.lastCall);
      
      // Lookup customer by phone
      customersApi.search(phone).then((results: any[]) => {
        if (results && results.length > 0) {
          const customer = results[0];
          console.log('📞 [MainLayout] Customer found:', customer);
          setCallerIdCustomer({
            id: customer.id,
            name: customer.name,
            phone: customer.phone,
            address: customer.address,
            notes: customer.notes,
          });
        } else {
          console.log('📞 [MainLayout] No customer found for:', phone);
          setCallerIdCustomer(null);
        }
      }).catch((error) => {
        console.error('📞 [MainLayout] Customer lookup error:', error);
        setCallerIdCustomer(null);
      });
    }
  }, [callerIdStatus.lastCall]);

  // Handle view customer action
  const handleViewCustomer = (customerId: string) => {
    dismissCallerModal();
    navigate(`/customers/${customerId}`);
  };

  // Handle new order action
  const handleNewOrder = (customerId: string) => {
    dismissCallerModal();
    navigate(`/order/new?customerId=${customerId}`);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Ücretsiz pakette yalnız patron/destek çalışır (R7). Denemesi biterken açık kalan kasiyer/şoför oturumu
  // kapatılır; giriş ekranı "yalnız patron" uyarısını gösterir (sunucu da belgeli istekleri 402 ile durdurur).
  const sessionRole = user?.role || localStorage.getItem('user_role');
  useEffect(() => {
    if (access?.tier === 'free' && user && sessionRole && !isOwnerRole(sessionRole)) {
      forceRelogin(PLAN_FREE_ROLE_NOTICE_KEY);
    }
  }, [access?.tier, user, sessionRole]);

  // Loading durumunda bir loading indicator göster (TÜM HOOK'LARDAN SONRA)
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  // Kapalı hesap: patron abonelik/ödeme dışındaki her yoldan abonelik sayfasına gider.
  // Patron dışı roller abonelik sayfasına giremez (ana sayfaya atar → döngü olurdu): sabit kilit ekranı + çıkış.
  {
    const closedView = closedAccountView(access, sessionRole);
    if (closedView === 'staff_locked') {
      const pui = planUiText(language);
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
          <div role="alert" className="max-w-md w-full bg-white rounded-2xl shadow-lg border border-red-200 p-8 text-center">
            <div className="text-5xl mb-4">🔒</div>
            <h2 className="text-xl font-bold text-red-600 mb-2">{pui.closedStaffTitle}</h2>
            <p className="text-gray-600 mb-6">{pui.closedStaffBody}</p>
            <Button onClick={handleLogout} className="bg-blue-600 hover:bg-blue-700 text-white">
              <LogOut className="w-4 h-4 mr-2" />
              {pui.logout}
            </Button>
          </div>
        </div>
      );
    }
    if (closedView === 'subscription' && !isClosedAllowedPath(location.pathname)) {
      return <Navigate to="/subscription" replace />;
    }
  }

  // Eleman: sadece randevu sayfasını görür, diğer menüler kapalı
  // Eleman kasiyerle aynı sayfaları görür (patrona özel alanlar hariç);
  // giriş yapınca doğrudan randevu ekranına düşer.
  if ((user?.role || localStorage.getItem('user_role')) === 'eleman' && location.pathname === '/') {
    return <Navigate to={carWashModuleEnabled ? '/carwash' : berberModuleEnabled ? '/barber' : '/sofawash'} replace />;
  }

  // Randevulu sektörler (oto yıkama, berber, koltuk yıkama): ana sayfa randevu ekranıdır.
  // Sipariş ekranı menüdeki "Yeni Sipariş" (/order/new) ile açılır. Sektör sunucudan teyit edilmeden yönlendirilmez.
  {
    const r = user?.role || localStorage.getItem('user_role');
    const isDriver = r === 'şoför' || r === 'sofor' || r === 'driver';
    const appointmentPath = carWashModuleEnabled ? '/carwash' : berberModuleEnabled ? '/barber' : sofaWashModuleEnabled ? '/sofawash' : null;
    if (serviceTypeConfirmed && appointmentPath && !isDriver && location.pathname === '/') {
      return <Navigate to={appointmentPath} replace />;
    }
  }

  // Şoför: kök dizinde kendi paneline yönlendir (tam dashboard'u görmesin)
  {
    const r = user?.role || localStorage.getItem('user_role');
    if ((r === 'şoför' || r === 'sofor' || r === 'driver') && location.pathname === '/') {
      return <Navigate to="/driver" replace />;
    }
  }

  let navigation = [
    { name: t('menu.dashboard'), href: '/', icon: LayoutDashboard },
    { name: t('menu.receipt'), href: '/receipt/new', icon: ReceiptText },
    { name: t('menu.orderNew'), href: '/order/new', icon: ShoppingCart },
    { name: t('menu.delivery'), href: '/delivery', icon: Truck },
    ...(carWashModuleEnabled ? [{ name: t('menu.carWash') || 'Oto Yıkama', href: '/carwash', icon: Car }] : []),
    ...(berberModuleEnabled ? [{ name: t('menu.barber') || 'Berber / Kuaför', href: '/barber', icon: Scissors }] : []),
    ...(sofaWashModuleEnabled ? [{ name: 'Randevu', href: '/sofawash', icon: Armchair }] : []),
    { name: t('menu.driverPickup'), href: '/driver/pickup', icon: Navigation },
    { name: t('menu.customers'), href: '/customers', icon: Users },
    { name: t('menu.products'), href: '/products', icon: Shirt },
    { name: t('menu.reportsList'), href: '/reports-list', icon: FileSpreadsheet },
    { name: t('menu.reportsCharts'), href: '/reports-charts', icon: PieChart },
    { name: t('menu.expenses'), href: '/expenses', icon: TrendingDown },
    { name: t('menu.subscription'), href: '/subscription', icon: Crown },
    { name: t('menu.referral') || 'Davet Et', href: '/referral', icon: Gift },
    { name: t('menu.payment') || 'Ödeme', href: '/payment', icon: CreditCard },
    // { name: t('menu.notifications'), href: '/templates', icon: Mail }, // DISABLED
    { name: t('menu.whatsappConnection'), href: '/whatsapp-connection', icon: MessageCircle },
    { name: t('menu.users'), href: '/users', icon: UserCog },
  ];
  
  // Role & permission checks
  const userRole = user?.role || localStorage.getItem('user_role') || '';
  const canViewReports = userRole === 'patron' || user?.permissions?.includes('reports') || user?.permissions?.includes('all');
  // Eleman, kasiyerle aynı kısıtlara tabi (patrona özel menüler gizli)
  const isKasiyer = userRole === 'kasiyer' || userRole === 'eleman';
  console.log('🔍 User role for permissions check:', userRole, ' | user object:', user?.role, ' | localStorage:', localStorage.getItem('user_role'));
  
  if (canViewReports) {
    navigation.push({ name: t('menu.zReport') || 'Z-Raporu', href: '/z-report', icon: Printer });
  }

  if (userRole === 'patron') {
    console.log('✅ Adding permissions menu for patron');
    navigation.push({ name: t('menu.permissions') || 'Yetkiler', href: '/permissions', icon: Shield });
    navigation.push({ name: 'Şubeler', href: '/branches', icon: Building2 });
  }
  
  navigation.push({ name: t('menu.settings'), href: '/settings', icon: Settings });
  navigation.push({ name: t('menu.hardware') || (tx(language, 'Donanım', 'Hardware')), href: '/hardware', icon: Package });

  // Şoför: yalnızca driver modülünü görür — kendi paneli + Alınacak Siparişler.
  // Fişe ürün ekleme, Alınacak Siparişler'deki "Sipariş Gir" ile yapılır. Başka menü yok.
  const isSofor = userRole === 'şoför' || userRole === 'sofor' || userRole === 'driver';
  if (isSofor) {
    navigation = [
      { name: t('menu.dashboard'), href: '/driver', icon: LayoutDashboard },
      { name: t('menu.driverPickup') || 'Alınacak Siparişler', href: '/driver/pickup', icon: Navigation },
    ];
  }

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Menü grupları varsayılan kapalı; durum cihazda hatırlanır, açık sayfanın grubu kendiliğinden açılır
  const MENU_GROUPS: Record<string, string[]> = {
    definitions: ['/customers', '/products', '/pricing', '/product-ordering', '/attributes', '/tailoring'],
    reports: ['/reports-list', '/reports-charts', '/expenses', '/z-report'],
    system: ['/subscription', '/whatsapp-connection', '/users', '/permissions', '/incoming-requests', '/branches', '/settings', '/hardware', '/tickets'],
  };
  const groupHasActive = (k: string) => (MENU_GROUPS[k] || []).some(r => isActive(r));
  const isGroupOpen = (k: string) => (openGroups[k] ?? false) || groupHasActive(k);
  const toggleGroup = (k: string) => setOpenGroups(g => {
    const next = { ...g, [k]: !isGroupOpen(k) };
    try { localStorage.setItem('cleandy:menuGroups', JSON.stringify(next)); } catch { /* depolama kapalıysa oturumluk */ }
    return next;
  });

  const GroupHeader = ({ id, label }: { id: string; label: string }) => (
    <button type="button" onClick={() => toggleGroup(id)}
      className="w-full flex items-center justify-between px-3 py-1.5 mt-3 rounded-md hover:bg-gray-50 transition-colors"
      aria-expanded={isGroupOpen(id)}>
      <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{label}</span>
      <ChevronDown className={`h-3.5 w-3.5 text-gray-400 transition-transform ${isGroupOpen(id) ? '' : '-rotate-90'}`} />
    </button>
  );

  // Şoför: soldaki büyük menü olmadan, sayfa tam ekran (şoför sayfaları kendi başlık/çıkışını taşır)
  if (isSofor) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Toaster position="top-right" />
        <Outlet />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" />

      {/* Caller ID Modal */}
      <IncomingCallModal
        call={currentCall}
        customer={callerIdCustomer}
        onClose={dismissCallerModal}
        onViewCustomer={handleViewCustomer}
        onNewOrder={handleNewOrder}
      />

      {/* Desktop Sidebar */}
      <TooltipProvider delayDuration={300}>
      <div className={`hidden lg:fixed lg:inset-y-0 lg:flex lg:flex-col transition-all duration-300 ${isSidebarCollapsed ? 'lg:w-20' : 'lg:w-64'}`}>
        <div className="flex flex-col flex-grow border-r border-gray-200 bg-white overflow-y-auto">
          {/* Logo */}
          <div className="flex items-center justify-between px-4 py-4 border-b border-gray-200">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              {!isSidebarCollapsed && (
                <div className="flex flex-col min-w-0">
                  <img
                    src={companyLogo || "/logo.jpg"}
                    alt={companyLogo ? "Company Logo" : "Cleandy"}
                    className="h-12 object-contain flex-shrink-0"
                  />
                  <span className="text-[10px] font-semibold text-gray-400 tracking-wide uppercase mt-0.5 truncate">
                    {serviceLabel}
                  </span>
                </div>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                const next = !isSidebarCollapsed;
                setIsSidebarCollapsed(next);
                localStorage.setItem('sidebarCollapsed', String(next));
              }}
              className="flex-shrink-0"
            >
              {isSidebarCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
            </Button>
          </div>

          {/* User Info */}
          <div className="px-4 py-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              {!isSidebarCollapsed && (
                <div className="flex-1 min-w-0">
                  {companyName && (
                    <p className="text-sm font-bold text-gray-800 truncate" title={companyName}>
                      {companyName}
                    </p>
                  )}
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {user?.username || user?.name || t('nav.user')}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {user?.tenantId}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-2 py-4 space-y-3">
            {/* Ana İşlemler - Büyük */}
            <div className="space-y-1">
              {[
                { to: '/order/new', icon: ShoppingCart, label: t('menu.orderNew'), active: isActive('/order/new') || location.pathname === '/' },
                { to: '/dashboard', icon: LayoutDashboard, label: t('menu.dashboard'), active: isActive('/dashboard'), badge: duyuruCount, badgeColor: 'bg-red-500' },
                { to: '/delivery-list', icon: ClipboardList, label: t('menu.deliveryList'), active: isActive('/delivery-list'), badge: hazirCount, badgeColor: 'bg-green-500' },
                { to: '/delivery', icon: Truck, label: t('menu.delivery'), active: isActive('/delivery'), badge: teslimCount, badgeColor: 'bg-blue-500' },
                ...(carWashModuleEnabled ? [{ to: '/carwash', icon: Car, label: t('menu.carWash') || 'Oto Yıkama', active: isActive('/carwash') }] : []),
                ...(berberModuleEnabled ? [{ to: '/barber', icon: Scissors, label: t('menu.barber') || 'Berber / Kuaför', active: isActive('/barber') }] : []),
                ...(sofaWashModuleEnabled ? [{ to: '/sofawash', icon: Armchair, label: 'Randevu', active: isActive('/sofawash') }] : []),
                { to: '/receipt/new', icon: ReceiptText, label: t('menu.receipt'), active: isActive('/receipt/new') },
              ].map(({ to, icon: Icon, label, active, badge, badgeColor }: any) => (
                <Tooltip key={to} open={isSidebarCollapsed ? undefined : false}>
                  <TooltipTrigger asChild>
                    <Link
                      to={to}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg text-base font-semibold transition-all ${active
                        ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg'
                        : 'text-gray-700 hover:bg-gray-100'
                      }`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <div className="relative flex-shrink-0">
                        <Icon className={`h-6 w-6 ${active ? 'text-white' : 'text-gray-500'}`} />
                        {badge > 0 && isSidebarCollapsed && (
                          <span className={`absolute -top-1.5 -right-1.5 ${badgeColor} text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-0.5`}>
                            {badge > 99 ? '99+' : badge}
                          </span>
                        )}
                      </div>
                      {!isSidebarCollapsed && <span className="flex-1">{label}</span>}
                      {!isSidebarCollapsed && badge > 0 && (
                        <span className={`${badgeColor} text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1`}>
                          {badge > 99 ? '99+' : badge}
                        </span>
                      )}
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="right" sideOffset={8}>{label}{badge > 0 ? ` (${badge})` : ''}</TooltipContent>
                </Tooltip>
              ))}
            </div>

            {/* Tanımlar - Küçük ve Gruplandırılmış */}
            {!isSidebarCollapsed && <GroupHeader id="definitions" label={t('menu.definitions')} />}
            <div className={`space-y-1 ${!isSidebarCollapsed && !isGroupOpen('definitions') ? 'hidden' : ''}`}>
              {[
                { to: '/customers', icon: Users, label: t('menu.customers') },
                { to: '/products', icon: Shirt, label: t('menu.products') },
                { to: '/pricing', icon: Tags, label: t('menu.pricing') },
                { to: '/product-ordering', icon: ListOrdered, label: t('menu.productOrdering') },
                { to: '/attributes', icon: SlidersHorizontal, label: t('menu.attributes') },
                { to: '/tailoring', icon: Scissors, label: t('menu.tailoring') },
              ].map(({ to, icon: Icon, label }) => (
                <Tooltip key={to} open={isSidebarCollapsed ? undefined : false}>
                  <TooltipTrigger asChild>
                    <Link
                      to={to}
                      className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${isActive(to) ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'}`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <Icon className={`h-4 w-4 flex-shrink-0 ${isActive(to) ? 'text-blue-600' : 'text-gray-400'}`} />
                      {!isSidebarCollapsed && <span>{label}</span>}
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="right" sideOffset={8}>{label}</TooltipContent>
                </Tooltip>
              ))}
              {!isKasiyer && (
                <Tooltip open={isSidebarCollapsed ? undefined : false}>
                  <TooltipTrigger asChild>
                    <Link
                      to="/users"
                      className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${isActive('/users') ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'}`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <UserCog className={`h-4 w-4 flex-shrink-0 ${isActive('/users') ? 'text-blue-600' : 'text-gray-400'}`} />
                      {!isSidebarCollapsed && <span>{t('menu.users')}</span>}
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="right" sideOffset={8}>{t('menu.users')}</TooltipContent>
                </Tooltip>
              )}
            </div>

            {/* Raporlar & Muhasebe - sadece patron / yetkili */}
            {canViewReports && (
              <>
                {!isSidebarCollapsed && <GroupHeader id="reports" label={t('menu.reportsAndAccounting')} />}
                <div className={`space-y-1 ${!isSidebarCollapsed && !isGroupOpen('reports') ? 'hidden' : ''}`}>
                  {[
                    { to: '/reports-list', icon: FileSpreadsheet, label: t('menu.reportsList') },
                    { to: '/reports-charts', icon: PieChart, label: t('menu.reportsCharts') },
                    { to: '/expenses', icon: TrendingDown, label: t('menu.expenses') },
                    { to: '/z-report', icon: Printer, label: t('menu.zReport') },
                  ].map(({ to, icon: Icon, label }) => (
                    <Tooltip key={to} open={isSidebarCollapsed ? undefined : false}>
                      <TooltipTrigger asChild>
                        <Link
                          to={to}
                          className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${isActive(to) ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'}`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <Icon className={`h-4 w-4 flex-shrink-0 ${isActive(to) ? 'text-blue-600' : 'text-gray-400'}`} />
                          {!isSidebarCollapsed && <span>{label}</span>}
                        </Link>
                      </TooltipTrigger>
                      <TooltipContent side="right" sideOffset={8}>{label}</TooltipContent>
                    </Tooltip>
                  ))}
                </div>
              </>
            )}

            {/* Sistem & Bildirimler - Küçük ve Gruplandırılmış */}
            {!isSidebarCollapsed && <GroupHeader id="system" label={t('menu.systemAndCommunication')} />}
            <div className={`space-y-1 ${!isSidebarCollapsed && !isGroupOpen('system') ? 'hidden' : ''}`}>
              {!isKasiyer && (
                <Tooltip open={isSidebarCollapsed ? undefined : false}>
                  <TooltipTrigger asChild>
                    <Link
                      to="/subscription"
                      className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${isActive('/subscription') ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'}`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <Crown className={`h-4 w-4 flex-shrink-0 ${isActive('/subscription') ? 'text-blue-600' : 'text-gray-400'}`} />
                      {!isSidebarCollapsed && <span>{t('menu.subscription')}</span>}
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="right" sideOffset={8}>{t('menu.subscription')}</TooltipContent>
                </Tooltip>
              )}

              {/* TEMPLATES MENU - DISABLED */}

              {!isKasiyer && (
                <Tooltip open={isSidebarCollapsed ? undefined : false}>
                  <TooltipTrigger asChild>
                    <Link
                      to="/whatsapp-connection"
                      className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${isActive('/whatsapp-connection') ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'}`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <MessageCircle className={`h-4 w-4 flex-shrink-0 ${isActive('/whatsapp-connection') ? 'text-blue-600' : 'text-gray-400'}`} />
                      {!isSidebarCollapsed && <span>{t('menu.whatsappConnection')}</span>}
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="right" sideOffset={8}>{t('menu.whatsappConnection')}</TooltipContent>
                </Tooltip>
              )}

              {!isKasiyer && (
                <Tooltip open={isSidebarCollapsed ? undefined : false}>
                  <TooltipTrigger asChild>
                    <Link
                      to="/users"
                      className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${isActive('/users') ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'}`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <UserCog className={`h-4 w-4 flex-shrink-0 ${isActive('/users') ? 'text-blue-600' : 'text-gray-400'}`} />
                      {!isSidebarCollapsed && <span>{t('menu.users')}</span>}
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="right" sideOffset={8}>{t('menu.users')}</TooltipContent>
                </Tooltip>
              )}

              {/* Permissions - sadece patron için */}
              {userRole === 'patron' && (
                <Tooltip open={isSidebarCollapsed ? undefined : false}>
                  <TooltipTrigger asChild>
                    <Link
                      to="/permissions"
                      className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${isActive('/permissions') ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'}`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <Shield className={`h-4 w-4 flex-shrink-0 ${isActive('/permissions') ? 'text-blue-600' : 'text-gray-400'}`} />
                      {!isSidebarCollapsed && <span>{t('menu.permissions')}</span>}
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="right" sideOffset={8}>{t('menu.permissions')}</TooltipContent>
                </Tooltip>
              )}

              {/* Gelen Talepler - temizim.app B2C entegrasyonu — sadece Türkçe */}
              {language === 'tr' && <Tooltip open={isSidebarCollapsed ? undefined : false}>
                <TooltipTrigger asChild>
                  <Link
                    to="/incoming-requests"
                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${isActive('/incoming-requests') ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <div className="relative flex-shrink-0">
                      <Inbox className={`h-4 w-4 ${isActive('/incoming-requests') ? 'text-blue-600' : 'text-gray-400'}`} />
                      {incomingCount > 0 && (
                        <span className="absolute -top-1.5 -right-1.5 min-w-[14px] h-[14px] bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center px-0.5 leading-none">
                          {incomingCount > 9 ? '9+' : incomingCount}
                        </span>
                      )}
                    </div>
                    {!isSidebarCollapsed && (
                      <span className="flex-1 flex items-center justify-between">
                        Gelen Talepler
                        {incomingCount > 0 && (
                          <span className="ml-1 bg-red-500 text-white text-xs font-bold rounded-full px-1.5 py-0.5 leading-none">
                            {incomingCount > 9 ? '9+' : incomingCount}
                          </span>
                        )}
                      </span>
                    )}
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="right" sideOffset={8}>Gelen Talepler</TooltipContent>
              </Tooltip>}

              {/* Şubeler - sadece patron için */}
              {userRole === 'patron' && (
                <Tooltip open={isSidebarCollapsed ? undefined : false}>
                  <TooltipTrigger asChild>
                    <Link
                      to="/branches"
                      className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${isActive('/branches') ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'}`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <Building2 className={`h-4 w-4 flex-shrink-0 ${isActive('/branches') ? 'text-blue-600' : 'text-gray-400'}`} />
                      {!isSidebarCollapsed && <span>Şubeler</span>}
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="right" sideOffset={8}>Şubeler</TooltipContent>
                </Tooltip>
              )}

              {!isKasiyer && (
                <Tooltip open={isSidebarCollapsed ? undefined : false}>
                  <TooltipTrigger asChild>
                    <Link
                      to="/settings"
                      className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${isActive('/settings') ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'}`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <Settings className={`h-4 w-4 flex-shrink-0 ${isActive('/settings') ? 'text-blue-600' : 'text-gray-400'}`} />
                      {!isSidebarCollapsed && <span>{t('menu.settings')}</span>}
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="right" sideOffset={8}>{t('menu.settings')}</TooltipContent>
                </Tooltip>
              )}

              <Tooltip open={isSidebarCollapsed ? undefined : false}>
                <TooltipTrigger asChild>
                  <Link
                    to="/hardware"
                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${isActive('/hardware') ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <Package className={`h-4 w-4 flex-shrink-0 ${isActive('/hardware') ? 'text-blue-600' : 'text-gray-400'}`} />
                    {!isSidebarCollapsed && <span>{t('menu.hardware') || (tx(language, 'Donanım', 'Hardware'))}</span>}
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="right" sideOffset={8}>{t('menu.hardware') || (tx(language, 'Donanım', 'Hardware'))}</TooltipContent>
              </Tooltip>

              {/* Destek talepleri — firma buradan Cleandy'ye talep açar */}
              <Tooltip open={isSidebarCollapsed ? undefined : false}>
                <TooltipTrigger asChild>
                  <Link
                    to="/tickets"
                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${isActive('/tickets') ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <LifeBuoy className={`h-4 w-4 flex-shrink-0 ${isActive('/tickets') ? 'text-blue-600' : 'text-gray-400'}`} />
                    {!isSidebarCollapsed && <span>{t('menu.support')}</span>}
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="right" sideOffset={8}>{t('menu.support')}</TooltipContent>
              </Tooltip>
            </div>
          </nav>

          {/* Logout Button */}
          <div className="px-2 py-4 border-t border-gray-200">
            <Button
              variant="ghost"
              className={`w-full ${isSidebarCollapsed ? 'justify-center' : 'justify-start'} text-gray-700 hover:text-gray-900 hover:bg-gray-100`}
              onClick={handleLogout}
            >
              <LogOut className="h-5 w-5 flex-shrink-0" />
              {!isSidebarCollapsed && <span className="ml-3">{t('nav.logout')}</span>}
            </Button>
          </div>
          
          {/* Version Info */}
          {!isSidebarCollapsed && (
            <div className="px-3 py-2 border-t border-gray-200 text-center">
              <Link to="/whats-new" className="group inline-flex flex-col items-center" onClick={() => setIsMobileMenuOpen(false)}>
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-600 group-hover:text-amber-700">
                  ✨ {whatsNewUi(language).menu}
                  {hasUnseenRelease() && !isActive('/whats-new') && (
                    <span className="rounded-full bg-red-500 text-white text-[10px] leading-none px-1.5 py-0.5">{whatsNewUi(language).newBadge}</span>
                  )}
                </span>
                <span className="text-xs text-gray-400 group-hover:text-gray-500">v{APP_VERSION} • {BUILD_DATE}</span>
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-50">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-2 min-w-0 flex-1 mr-2">
            {companyLogo ? (
              <img
                src={companyLogo}
                alt="Company Logo"
                className="h-8 object-contain flex-shrink-0"
              />
            ) : (
              <img
                src="/logo.jpg"
                alt="Logo"
                className="h-8 w-8 object-contain flex-shrink-0"
              />
            )}
            {(companyName || user?.name) && (
              <div className="min-w-0 flex flex-col leading-tight">
                {companyName && (
                  <span className="text-sm font-bold text-gray-800 truncate" title={companyName}>
                    {companyName}
                  </span>
                )}
                {user?.name && (
                  <span className="text-xs text-gray-500 truncate" title={user.name}>
                    {user.name}{user.role ? ` · ${user.role}` : ''}
                  </span>
                )}
              </div>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </Button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="border-t border-gray-200 bg-white max-h-[80vh] overflow-y-auto">
            <nav className="px-2 py-3 space-y-1">

              {/* ── Ana İşlemler ── */}
              <div className="space-y-1 pb-2">
                {[
                  { to: '/order/new', icon: ShoppingCart, label: t('menu.orderNew') },
                  { to: '/dashboard', icon: LayoutDashboard, label: t('menu.dashboard') },
                  { to: '/delivery-list', icon: ClipboardList, label: t('menu.deliveryList') },
                  { to: '/delivery', icon: Truck, label: t('menu.delivery') },
                  ...(carWashModuleEnabled ? [{ to: '/carwash', icon: Car, label: t('menu.carWash') || 'Oto Yıkama' }] : []),
                  ...(berberModuleEnabled ? [{ to: '/barber', icon: Scissors, label: t('menu.barber') || 'Berber / Kuaför' }] : []),
                  ...(sofaWashModuleEnabled ? [{ to: '/sofawash', icon: Armchair, label: 'Randevu' }] : []),
                  { to: '/receipt/new', icon: ReceiptText, label: t('menu.receipt') },
                ].map(({ to, icon: Icon, label }) => (
                  <Link key={to} to={to}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors ${isActive(to) ? 'bg-blue-500 text-white' : 'text-gray-800 hover:bg-gray-100'}`}
                    onClick={() => setIsMobileMenuOpen(false)}>
                    <Icon className={`h-5 w-5 flex-shrink-0 ${isActive(to) ? 'text-white' : 'text-gray-500'}`} />
                    <span>{label}</span>
                  </Link>
                ))}
              </div>

              {/* ── Tanımlar ── */}
              <details className="group">
                <summary className="flex items-center justify-between px-3 py-2 cursor-pointer rounded-md hover:bg-gray-50 list-none select-none">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{t('menu.definitions')}</span>
                  <ChevronDown className="h-3.5 w-3.5 text-gray-400 group-open:rotate-180 transition-transform" />
                </summary>
                <div className="mt-1 space-y-0.5">
                  {[
                    { to: '/customers', icon: Users, label: t('menu.customers') },
                    { to: '/products', icon: Shirt, label: t('menu.products') },
                    { to: '/pricing', icon: Tags, label: t('menu.pricing') },
                    { to: '/product-ordering', icon: ListOrdered, label: t('menu.productOrdering') },
                    { to: '/attributes', icon: SlidersHorizontal, label: t('menu.attributes') },
                    { to: '/tailoring', icon: Scissors, label: t('menu.tailoring') },
                    ...(!isKasiyer ? [{ to: '/users', icon: UserCog, label: t('menu.users') }] : []),
                  ].map(({ to, icon: Icon, label }) => (
                    <Link key={to} to={to}
                      className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${isActive(to) ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'}`}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      <Icon className={`h-4 w-4 flex-shrink-0 ${isActive(to) ? 'text-blue-600' : 'text-gray-400'}`} />
                      <span>{label}</span>
                    </Link>
                  ))}
                </div>
              </details>

              {/* ── Raporlar & Muhasebe ── */}
              {canViewReports && (
                <details className="group">
                  <summary className="flex items-center justify-between px-3 py-2 cursor-pointer rounded-md hover:bg-gray-50 list-none select-none">
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{t('menu.reportsAndAccounting')}</span>
                    <ChevronDown className="h-3.5 w-3.5 text-gray-400 group-open:rotate-180 transition-transform" />
                  </summary>
                  <div className="mt-1 space-y-0.5">
                    {[
                      { to: '/reports-list', icon: FileSpreadsheet, label: t('menu.reportsList') },
                      { to: '/reports-charts', icon: PieChart, label: t('menu.reportsCharts') },
                      { to: '/expenses', icon: TrendingDown, label: t('menu.expenses') },
                      { to: '/z-report', icon: Printer, label: t('menu.zReport') },
                    ].map(({ to, icon: Icon, label }) => (
                      <Link key={to} to={to}
                        className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${isActive(to) ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'}`}
                        onClick={() => setIsMobileMenuOpen(false)}>
                        <Icon className={`h-4 w-4 flex-shrink-0 ${isActive(to) ? 'text-blue-600' : 'text-gray-400'}`} />
                        <span>{label}</span>
                      </Link>
                    ))}
                  </div>
                </details>
              )}

              {/* ── Sistem & İletişim ── */}
              <details className="group">
                <summary className="flex items-center justify-between px-3 py-2 cursor-pointer rounded-md hover:bg-gray-50 list-none select-none">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{t('menu.systemAndCommunication')}</span>
                  <ChevronDown className="h-3.5 w-3.5 text-gray-400 group-open:rotate-180 transition-transform" />
                </summary>
                <div className="mt-1 space-y-0.5">
                  {!isKasiyer && (
                    <Link to="/subscription"
                      className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${isActive('/subscription') ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'}`}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      <Crown className={`h-4 w-4 flex-shrink-0 ${isActive('/subscription') ? 'text-blue-600' : 'text-gray-400'}`} />
                      <span>{t('menu.subscription')}</span>
                    </Link>
                  )}
                  {!isKasiyer && (
                    <Link to="/whatsapp-connection"
                      className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${isActive('/whatsapp-connection') ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'}`}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      <MessageCircle className={`h-4 w-4 flex-shrink-0 ${isActive('/whatsapp-connection') ? 'text-blue-600' : 'text-gray-400'}`} />
                      <span>{t('menu.whatsappConnection')}</span>
                    </Link>
                  )}
                  {language === 'tr' && (
                    <Link to="/incoming-requests"
                      className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${isActive('/incoming-requests') ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'}`}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      <div className="relative flex-shrink-0">
                        <Inbox className={`h-4 w-4 ${isActive('/incoming-requests') ? 'text-blue-600' : 'text-gray-400'}`} />
                        {incomingCount > 0 && (
                          <span className="absolute -top-1.5 -right-1.5 min-w-[14px] h-[14px] bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center px-0.5">
                            {incomingCount > 9 ? '9+' : incomingCount}
                          </span>
                        )}
                      </div>
                      <span className="flex-1 flex items-center justify-between">
                        Gelen Talepler
                        {incomingCount > 0 && (
                          <span className="bg-red-500 text-white text-xs font-bold rounded-full px-1.5 py-0.5">{incomingCount > 9 ? '9+' : incomingCount}</span>
                        )}
                      </span>
                    </Link>
                  )}
                  {userRole === 'patron' && (
                    <Link to="/permissions"
                      className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${isActive('/permissions') ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'}`}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      <Shield className={`h-4 w-4 flex-shrink-0 ${isActive('/permissions') ? 'text-blue-600' : 'text-gray-400'}`} />
                      <span>{t('menu.permissions')}</span>
                    </Link>
                  )}
                  {userRole === 'patron' && (
                    <Link to="/branches"
                      className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${isActive('/branches') ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'}`}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      <Building2 className={`h-4 w-4 flex-shrink-0 ${isActive('/branches') ? 'text-blue-600' : 'text-gray-400'}`} />
                      <span>Şubeler</span>
                    </Link>
                  )}
                  {!isKasiyer && (
                    <Link to="/settings"
                      className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${isActive('/settings') ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'}`}
                      onClick={() => setIsMobileMenuOpen(false)}>
                      <Settings className={`h-4 w-4 flex-shrink-0 ${isActive('/settings') ? 'text-blue-600' : 'text-gray-400'}`} />
                      <span>{t('menu.settings')}</span>
                    </Link>
                  )}
                  <Link to="/hardware"
                    className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${isActive('/hardware') ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'}`}
                    onClick={() => setIsMobileMenuOpen(false)}>
                    <Package className={`h-4 w-4 flex-shrink-0 ${isActive('/hardware') ? 'text-blue-600' : 'text-gray-400'}`} />
                    <span>{t('menu.hardware') || (tx(language, 'Donanım', 'Hardware'))}</span>
                  </Link>
                  <Link to="/tickets"
                    className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${isActive('/tickets') ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'}`}
                    onClick={() => setIsMobileMenuOpen(false)}>
                    <LifeBuoy className={`h-4 w-4 flex-shrink-0 ${isActive('/tickets') ? 'text-blue-600' : 'text-gray-400'}`} />
                    <span>{t('menu.support')}</span>
                  </Link>
                </div>
              </details>

              {/* ── Çıkış & Versiyon ── */}
              <div className="pt-2 border-t border-gray-200">
                {!isSidebarCollapsed && (
                  <div className="px-1 pb-2"><InstallAppButton className="w-full justify-center" /></div>
                )}
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-red-600 hover:bg-red-50"
                >
                  <LogOut className="h-4 w-4" />
                  <span>{t('nav.logout')}</span>
                </button>
                <Link to="/whats-new" className="block text-center pt-2" onClick={() => setIsMobileMenuOpen(false)}>
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-600">
                    ✨ {whatsNewUi(language).menu}
                    {hasUnseenRelease() && !isActive('/whats-new') && (
                      <span className="rounded-full bg-red-500 text-white text-[10px] leading-none px-1.5 py-0.5">{whatsNewUi(language).newBadge}</span>
                    )}
                  </span>
                  <span className="block text-xs text-gray-400">v{APP_VERSION} • {BUILD_DATE}</span>
                </Link>
              </div>

            </nav>
          </div>
        )}
      </div>
      </TooltipProvider>

      {/* Main Content */}
      <div className={`${isSidebarCollapsed ? 'lg:pl-20' : 'lg:pl-64'} transition-all duration-300`}>
        <PlanBanners />
        <main className="pt-16 lg:pt-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

// Paket durumu sağlayıcısı + "Satın al" penceresi tüm korumalı sayfaları sarar
function MainLayout() {
  const { user } = useAuth();
  const enabled = !!(user || localStorage.getItem('user'));
  return (
    <PlanProvider enabled={enabled}>
      <MainLayoutComponent />
    </PlanProvider>
  );
}

export { MainLayout };